/**
 * Stores a Response object within an Error, so that the application
 * can access the response when an error is thrown.
 *
 * For example, a `queryFn` can throw the error like so:
 *
 * if (!response.ok) throw new ResponseError(response.statusText, response)
 *
 * which should cause the Error to be thrown from within the loader.
 *
 * Then an ErrorBoundary can consume the route error and access the response, like so:
 *
 * const routeError = useRouteError()
 * const responseStatus = isResponseError(routeError) ? routeError.response.status : undefined
 *
 */
export class ResponseError extends Error {
  readonly response: Response
  constructor(message: string, response: Response) {
    super(message)
    this.response = response
    // Keep the prototype.name fixed even after minification.
    this.name = 'ResponseError'
  }
}

/**
 * Type guard for checking to see if a parameter is a ResponseError object
 */
export function isResponseError(e: unknown): e is ResponseError {
  return e instanceof ResponseError
}

/**
 * Named `ResponseError` subclasses whose `.name` matches the expected-error
 * allowlist in `@github-ui/failbot`. Throwing these instead of a bare
 * `ResponseError` lets failbot silence content-negotiation and environmental
 * responses (406, 429, 503, CSRF rejections, proxy/CDN noise) that would
 * otherwise be reported to Sentry as critical errors.
 *
 * They remain `instanceof ResponseError`, so the route error boundary UI and
 * document-title logic are unaffected. This mirrors the status→error mapping
 * already used by the GraphQL fetch path (`packages/fetch-graphql/fetch-graphql.ts`).
 *
 * The `name` is assigned explicitly (rather than relying on the class name) so
 * it survives minification, since failbot matches on the string value.
 */
export class NotAcceptableError extends ResponseError {
  constructor(response: Response) {
    super(response.statusText, response)
    this.name = 'NotAcceptableError'
  }
}

export class RateLimitError extends ResponseError {
  constructor(response: Response) {
    super(response.statusText, response)
    this.name = 'RateLimitError'
  }
}

export class ServiceUnavailableError extends ResponseError {
  constructor(response: Response) {
    super(response.statusText, response)
    this.name = 'ServiceUnavailableError'
  }
}

export class SecFetchHeaderError extends ResponseError {
  constructor(response: Response) {
    super(response.statusText, response)
    this.name = 'SecFetchHeaderError'
  }
}

export class NoiseError extends ResponseError {
  constructor(response: Response) {
    super(response.statusText, response)
    this.name = 'NoiseError'
  }
}

/**
 * Maps a non-OK `Response` to the most specific {@link ResponseError} subclass,
 * so failbot can silence expected/non-actionable statuses. Unmapped statuses
 * (e.g. 500) fall back to the base `ResponseError`, which is still reported.
 */
export async function responseErrorForStatus(response: Response): Promise<ResponseError> {
  switch (response.status) {
    case 406:
      return new NotAcceptableError(response)
    case 429:
      return new RateLimitError(response)
    case 503:
      return new ServiceUnavailableError(response)
    // 410 Gone and 418 are non-actionable environmental noise
    // (deprecated endpoints, bots). Mirrors the GraphQL path's isNoiseError.
    case 410:
    case 418:
      return new NoiseError(response)
  }

  if (response.status === 403) {
    // UI Service CSRF rejections surface via the X-Reject-Reason header.
    if (response.headers.get('X-Reject-Reason') === 'csrf') {
      return new SecFetchHeaderError(response)
    }
    // Cloudflare bot-protection blocks on custom domains pointed at GitHub.
    if ((await safeResponseText(response)).toLowerCase().includes('cloudflare')) {
      return new NoiseError(response)
    }
  }

  if (response.status === 422) {
    const text = await safeResponseText(response)
    if (text.includes('sec-fetch-dest') || text.includes('sec-fetch-site')) {
      return new SecFetchHeaderError(response)
    }
  }

  // Everything else falls through to the base ResponseError, which failbot
  // still reports: genuine 5xx server errors, plus auth/not-found statuses.
  // The statuses mapped above deliberately mirror fetch-graphql's
  // assertNoHttpErrors/isNoiseError so both fetch paths suppress the same
  // expected/non-actionable errors; anything it doesn't classify stays
  // reported here too.
  return new ResponseError(response.statusText, response)
}

/**
 * Reads a clone of the response body as text without consuming the original
 * response (which stays attached to the thrown error). Returns an empty string
 * if the body cannot be read.
 */
async function safeResponseText(response: Response): Promise<string> {
  try {
    return await response.clone().text()
  } catch {
    return ''
  }
}
